import { computed, ref, toValue, watch, type MaybeRefOrGetter, type Ref } from 'vue';
import { AxiosError } from 'axios';
import type { TableColumnStaticProps } from '@/types/tableTypes';
import {
    builtInLayout,
    chooseLayout,
    type RecordLayoutDefinition,
} from '@/composables/recordLayout/recordLayoutDefinition';
import {
    createRecordLayoutClient,
    httpRecordLayoutTransport,
    type RecordLayoutTransport,
} from '@/composables/recordLayout/recordLayoutClient';
import { useAuthStore } from '@/stores/authStore';

/**
 * A record page's layout, and the Customize dialog's two ways of changing it.
 *
 * - "Just me": every change shows at once and saves after a short pause. A failed
 *   save keeps the change on screen and says so, with Retry. Reset deletes your
 *   layout, so Everyone's default (or the built-in one) shows again.
 * - "Everyone's default" (developers): changes go to a draft that the page
 *   previews. Nothing is saved until "Save as default". Cancel drops the draft.
 *
 * A layout that cannot be loaded shows the built-in one, with a message.
 *
 * ONE COPY PER RECORD TYPE. The record page, the side peek and the split pane
 * each call this, often for the same record type at once. They share one copy
 * of the person's layout and one save client, per signed-in user. Separate
 * copies drifted: a chip removed in the peek still showed in the pane, and
 * the pane's next save wrote its stale copy back over the peek's change. The
 * shared copy also loads once, instead of on every peek. Callers that pass the
 * same test transport share a copy the same way; a fresh transport is a fresh copy.
 */

export const LOAD_FAILED_MESSAGE = "Couldn't load your layout. Showing the default.";
export const SAVE_FAILED_MESSAGE = "Couldn't save. Your change is only on this screen.";
export const DEFAULT_FORBIDDEN_MESSAGE = 'Only developers can change the default.';

function clone(layout: RecordLayoutDefinition): RecordLayoutDefinition {
    return JSON.parse(JSON.stringify(layout)) as RecordLayoutDefinition;
}

export interface UseRecordLayoutOptions {
    /** Stands in for the HTTP calls in tests. */
    transport?: RecordLayoutTransport;
    delayMs?: number;
    /** Called after the server refuses a default save with 403, to refresh who the user is. */
    onForbidden?: () => void;
}

type RecordLayoutClient = ReturnType<typeof createRecordLayoutClient>;

interface SharedLayout {
    mine: Ref<RecordLayoutDefinition | null>;
    everyonesDefault: Ref<RecordLayoutDefinition | null>;
    loaded: Ref<boolean>;
    /** The load in flight or done, so every caller waits for the same one. */
    loading: Promise<void> | null;
}

const layoutsByClient = new WeakMap<Promise<RecordLayoutClient>, Map<string, SharedLayout>>();
let httpClient: Promise<RecordLayoutClient> | null = null;
const clientsByTransport = new WeakMap<RecordLayoutTransport, Promise<RecordLayoutClient>>();

/** One client per transport, so callers on the same transport share a copy, as the app's callers do. */
function clientFor(transport: RecordLayoutTransport, delayMs: number | undefined): Promise<RecordLayoutClient> {
    let client = clientsByTransport.get(transport);
    if (!client) {
        client = Promise.resolve(createRecordLayoutClient(transport, delayMs));
        clientsByTransport.set(transport, client);
    }
    return client;
}

function sharedLayout(clientPromise: Promise<RecordLayoutClient>, key: string): SharedLayout {
    let layouts = layoutsByClient.get(clientPromise);
    if (!layouts) {
        layouts = new Map();
        layoutsByClient.set(clientPromise, layouts);
    }
    let layout = layouts.get(key);
    if (!layout) {
        layout = { mine: ref(null), everyonesDefault: ref(null), loaded: ref(false), loading: null };
        layouts.set(key, layout);
    }
    return layout;
}

export function useRecordLayout(
    entityId: MaybeRefOrGetter<string>,
    columns: MaybeRefOrGetter<TableColumnStaticProps[]>,
    options: UseRecordLayoutOptions = {},
) {
    const clientPromise = options.transport ? clientFor(options.transport, options.delayMs) : (httpClient ??= httpRecordLayoutTransport().then((transport) => createRecordLayoutClient(transport, options.delayMs)));
    // The shared copy is per signed-in user, so the next person never sees the last one's layout.
    const userKey = options.transport ? 'test' : String(useAuthStore().userData?.id ?? 'anonymous');
    const shared = computed(() => sharedLayout(clientPromise, `${userKey}:${toValue(entityId)}`));

    const mine = computed({
        get: () => shared.value.mine.value,
        set: (layout: RecordLayoutDefinition | null) => (shared.value.mine.value = layout),
    });
    const everyonesDefault = computed({
        get: () => shared.value.everyonesDefault.value,
        set: (layout: RecordLayoutDefinition | null) => (shared.value.everyonesDefault.value = layout),
    });
    const loaded = computed(() => shared.value.loaded.value);
    const message = ref('');
    const defaultDraft = ref<RecordLayoutDefinition | null>(null);
    let lastFailedSave: RecordLayoutDefinition | null = null;

    const builtIn = computed(() => builtInLayout(toValue(entityId), toValue(columns)));
    const effective = computed(() => chooseLayout(mine.value, everyonesDefault.value, builtIn.value));
    /** What the page draws: the default draft while a developer edits it, else the chosen layout. */
    const shown = computed(() => defaultDraft.value ?? effective.value);

    /** Loads the shared copy once. `force` loads it again, for Reload. A failed load is retried next time. */
    async function load(force = false) {
        // No record type yet, such as a list page whose split pane is not showing: nothing to load.
        if (toValue(entityId) === '') return;
        const layout = shared.value;
        message.value = '';
        if (!force && layout.loading) {
            await layout.loading;
            return;
        }
        const requestedEntity = toValue(entityId);
        layout.loaded.value = false;
        layout.loading = (async () => {
            try {
                const pair = await (await clientPromise).load(requestedEntity);
                layout.mine.value = pair.mine;
                layout.everyonesDefault.value = pair.default;
            } catch {
                layout.mine.value = null;
                layout.everyonesDefault.value = null;
                layout.loading = null;
                message.value = LOAD_FAILED_MESSAGE;
            } finally {
                layout.loaded.value = true;
            }
        })();
        await layout.loading;
    }

    watch(() => toValue(entityId), () => load(), { immediate: true });

    async function saveMine(layout: RecordLayoutDefinition) {
        mine.value = layout;
        message.value = '';
        try {
            await (await clientPromise).saveMine(toValue(entityId), layout);
            lastFailedSave = null;
        } catch {
            lastFailedSave = layout;
            message.value = SAVE_FAILED_MESSAGE;
        }
    }

    function retrySave() {
        if (lastFailedSave) {
            void saveMine(lastFailedSave);
        }
    }

    async function resetMine() {
        mine.value = null;
        message.value = '';
        lastFailedSave = null;
        try {
            await (await clientPromise).resetMine(toValue(entityId));
        } catch {
            message.value = SAVE_FAILED_MESSAGE;
        }
    }

    function startEditingDefault() {
        defaultDraft.value = clone(everyonesDefault.value ?? builtIn.value);
    }

    function updateDefaultDraft(layout: RecordLayoutDefinition) {
        defaultDraft.value = layout;
    }

    function cancelDefault() {
        defaultDraft.value = null;
        message.value = '';
    }

    async function saveDefault(): Promise<boolean> {
        if (!defaultDraft.value) return false;
        const draft = defaultDraft.value;
        try {
            await (await clientPromise).saveDefault(toValue(entityId), draft);
            everyonesDefault.value = draft;
            defaultDraft.value = null;
            message.value = '';
            return true;
        } catch (error) {
            if (error instanceof AxiosError && error.response?.status === 403) {
                message.value = DEFAULT_FORBIDDEN_MESSAGE;
                defaultDraft.value = null;
                options.onForbidden?.();
            } else {
                message.value = SAVE_FAILED_MESSAGE;
            }
            return false;
        }
    }

    return {
        loaded,
        message,
        mine,
        everyonesDefault,
        builtIn,
        effective,
        shown,
        isEditingDefault: computed(() => defaultDraft.value !== null),
        saveMine,
        retrySave,
        resetMine,
        startEditingDefault,
        updateDefaultDraft,
        cancelDefault,
        saveDefault,
        reload: () => load(true),
    };
}
